import { Flex } from 'antd'
import { CSSProperties, FC } from 'react'
import { useTranslation } from 'react-i18next'
import { DeltaComponent } from './DeltaComponent'
import { SensorBGC } from './sensors'
import { SBV } from './sensors/SBV'
import { CHV } from './sensors/CHV'
import { CHM } from './sensors/CHM'
import { sensorStore } from '../store/sensor'

interface Props {
	carrierId: number
}

export const CarrierOneSettingsPCHComp: FC<Props> = ({
	carrierId
}) => {
	const { t } = useTranslation()
	const { sensors } = sensorStore()

	const getSensorData = (code: string) => {
		return sensors
			.find((s) => s.code === code)
			?.sensorData.find((s) => s.carrierId === carrierId)
	}

	const finderColor = (code: string) => {
		return getSensorData(code)?.color || 'gray'
	}

	const getStatusValue = (code: string) => {
		return getSensorData(code)?.value
	}

	const getNumericStatusValue = (code: string) => {
		const value = Number(getStatusValue(code))
		return isNaN(value) ? 0 : value
	}

	const finderFC = (code: string) => {
		return getNumericStatusValue(code)
	}

	const resolveStatusLabel = (
		code: string,
		labels: { [key: string]: string | undefined }
	) => {
		const value = getStatusValue(code) ?? '0'
		return labels[value] ?? labels['0'] ?? ''
	}

	const CarrierOneSettingsInnerBlockStyle: CSSProperties = {
		backgroundColor: '#fff',
		padding: 10,
		borderRadius: '6px'
	}
	return (
		<Flex gap={10} vertical>
			<Flex gap={10}>
				<Flex gap={10} vertical>
					{/*  style={{ width: '100%' }}> */}
					<Flex
						gap={10}
						style={CarrierOneSettingsInnerBlockStyle}
						vertical>
						<h3 style={{ margin: 0 }}>{t('Status')}</h3>
						<Flex gap={8}>
							<SensorBGC
								color={finderColor('sts_stopDC-DC')}
								text={resolveStatusLabel(
									'sts_stopDC-DC',
									{
										'0': 'StopDC-DC',
										'1': 'ChargeCapacitor',
										'2': 'StartDC-DC',
										'3': 'WorkDC-DC',
										'4': 'Work380V'
									}
								)}
							/>
							<SensorBGC
								color={finderColor(
									'sts_not_enough_voltage'
								)}
								text={resolveStatusLabel(
									'sts_not_enough_voltage',
									{
										'0': 'NotEnoughVoltage'
									}
								)}
							/>
							<SensorBGC
								color={finderColor(
									'sts_pch_drawer_open'
								)}
								text={resolveStatusLabel(
									'sts_pch_drawer_open',
									{
										'0': 'PCHDrawerOpen'
									}
								)}
							/>
						</Flex>
						<Flex gap={8}>
							<SensorBGC
								color={finderColor('sts_stop_supply')}
								text={resolveStatusLabel(
									'sts_stop_supply',
									{
										'0': 'StopSupply',
										'1': 'StartSupply',
										'2': 'WorkSupply'
									}
								)}
							/>
							<SensorBGC
								color={finderColor('sts_redirecting')}
								text={resolveStatusLabel(
									'sts_redirecting',
									{
										'0': 'Overvoltage'
									}
								)}
							/>
							<SensorBGC
								color={finderColor('sts_DC-DCFault')}
								text={resolveStatusLabel(
									'sts_DC-DCFault',
									{
										'0': 'DC-DCFault'
									}
								)}
							/>
						</Flex>
						<Flex gap={8}>
							<SensorBGC
								color={finderColor(
									'sts_stop_condensator'
								)}
								text={resolveStatusLabel(
									'sts_stop_condensator',
									{
										'0': 'StopCondensator',
										'1': 'StartCondensator',
										'2': 'WorkCondensator'
									}
								)}
							/>
							<SensorBGC
								color={finderColor(
									'sts_high_current'
								)}
								text={resolveStatusLabel(
									'sts_high_current',
									{
										'0': 'HighCurrent'
									}
								)}
							/>
							<SensorBGC
								color={finderColor('sts_overheat75')}
								text={resolveStatusLabel(
									'sts_overheat75',
									{
										'0': 'Overheat75'
									}
								)}
							/>
						</Flex>
						<Flex gap={8}>
							<SensorBGC
								color={finderColor(
									'sts_stop_compressor'
								)}
								text={resolveStatusLabel(
									'sts_stop_compressor',
									{
										'0': 'StopCompressor',
										'1': 'StartCompressor',
										'2': 'WorkCompressor'
									}
								)}
							/>
							<SensorBGC
								color={finderColor(
									'sts_protection_temp'
								)}
								text={resolveStatusLabel(
									'sts_protection_temp',
									{
										'0': 'ProtectionTemp'
									}
								)}
							/>
							<SensorBGC
								color={finderColor(
									'sts_analog_module'
								)}
								text={resolveStatusLabel(
									'sts_analog_module',
									{
										'0': 'AnalogModule'
									}
								)}
							/>
						</Flex>
					</Flex>
					<Flex
						gap={10}
						style={CarrierOneSettingsInnerBlockStyle}
						vertical>
						<h3 style={{ margin: 0 }}>
							{t('Monitoring')} DC-DC 450
						</h3>
						<Flex gap={10}>
							<Flex gap={10} vertical>
								<SBV
									name="inputVoltage"
									value={getNumericStatusValue(
										'input_voltage'
									)}
									measurement="Volt"
								/>
								<SBV
									name="DC-DCVoltage"
									value={getNumericStatusValue(
										'dc_dcvoltage'
									)}
									measurement="Volt"
								/>
							</Flex>
							<div
								style={{
									height: '100%',
									backgroundColor: '#DDE2EB',
									width: 1
								}}></div>
							<Flex gap={10} vertical>
								<SBV
									name="InputAmper"
									value={getNumericStatusValue(
										'input_amper'
									)}
									measurement="Amp"
								/>
								<SBV
									name="DrawerTemp"
									value={getNumericStatusValue(
										'drawer_temp'
									)}
									measurement="C&deg;"
								/>
							</Flex>
						</Flex>
					</Flex>
					<Flex
						gap={10}
						style={CarrierOneSettingsInnerBlockStyle}
						vertical>
						<h3 style={{ margin: 0 }}>{t('DELIXI')}</h3>
						<Flex gap={10}>
							<Flex
								gap={10}
								vertical
								style={{ flex: 1 }}>
								<Flex gap={10} align="center">
									<h3
										style={{
											margin: 0,
											flex: 1
										}}>
										CH1
									</h3>
									<b>
										{(() => {
											const status =
												getStatusValue(
													'ch1_status'
												)
											return !status ||
												status === '0' ? (
												<span
													style={{
														color: '#74C24F'
													}}>
													{t('NoAccidents')}
												</span>
											) : (
												<span
													style={{
														color: '#F05742'
													}}>
													{t('ERR') +
														status}
												</span>
											)
										})()}
									</b>
								</Flex>
								<SBV
									name="Chastota"
									value={getNumericStatusValue(
										'ch1_chastota'
									)}
									isPCH
									measurement="Hz"
								/>
								<SBV
									name="Voltage"
									value={getNumericStatusValue(
										'ch1_voltage'
									)}
									isPCH
									measurement="Volt"
								/>
								<SBV
									name="Current"
									value={getNumericStatusValue(
										'ch1_current'
									)}
									isPCH
									measurement="Amp"
								/>
							</Flex>
							<div
								style={{
									height: '100%',
									backgroundColor: '#DDE2EB',
									width: 1
								}}></div>
							<Flex
								gap={10}
								vertical
								style={{ flex: 1 }}>
								<Flex gap={10} align="center">
									<h3
										style={{
											margin: 0,
											flex: 1
										}}>
										CH2
									</h3>
									<b>
										{(() => {
											const status =
												getStatusValue(
													'ch2_status'
												)
											return !status ||
												status === '0' ? (
												<span
													style={{
														color: '#74C24F'
													}}>
													{t('NoAccidents')}
												</span>
											) : (
												<span
													style={{
														color: '#F05742'
													}}>
													{t('ERR') +
														status}
												</span>
											)
										})()}
									</b>
								</Flex>
								<SBV
									name="Chastota"
									value={getNumericStatusValue(
										'ch2_chastota'
									)}
									isPCH
									measurement="Hz"
								/>
								<SBV
									name="Voltage"
									value={getNumericStatusValue(
										'ch2_voltage'
									)}
									isPCH
									measurement="Volt"
								/>
								<SBV
									name="Current"
									value={getNumericStatusValue(
										'ch2_current'
									)}
									isPCH
									measurement="Amp"
								/>
							</Flex>
							<div
								style={{
									height: '100%',
									backgroundColor: '#DDE2EB',
									width: 1
								}}></div>
							<Flex
								gap={10}
								vertical
								style={{ flex: 1 }}>
								<Flex gap={10} align="center">
									<h3
										style={{
											margin: 0,
											flex: 1
										}}>
										CH3
									</h3>
									<b>
										{(() => {
											const status =
												getStatusValue(
													'ch3_status'
												)
											return !status ||
												status === '0' ? (
												<span
													style={{
														color: '#74C24F'
													}}>
													{t('NoAccidents')}
												</span>
											) : (
												<span
													style={{
														color: '#F05742'
													}}>
													{t('ERR') +
														status}
												</span>
											)
										})()}
									</b>
								</Flex>
								<SBV
									name="Chastota"
									value={getNumericStatusValue(
										'ch3_chastota'
									)}
									isPCH
									measurement="Hz"
								/>
								<SBV
									name="Voltage"
									value={getNumericStatusValue(
										'ch3_voltage'
									)}
									isPCH
									measurement="Volt"
								/>
								<SBV
									name="Current"
									value={getNumericStatusValue(
										'ch3_current'
									)}
									isPCH
									measurement="Amp"
								/>
							</Flex>
						</Flex>
					</Flex>
				</Flex>
				<Flex
					gap={10}
					style={{
						...CarrierOneSettingsInnerBlockStyle,
						// width: 241,
						overflow: 'auto'
					}}>
					<DeltaComponent carrierId={+carrierId} isPCH />
				</Flex>
			</Flex>
			<Flex
				gap={10}
				style={CarrierOneSettingsInnerBlockStyle}
				vertical>
				<h3 style={{ margin: 0 }}>DC-DC 450</h3>
				<Flex gap={10}>
					<CHM
						multiplier={
							1
							// isNaN(
							// Number(
							// getStatusValue('max_voltage_1')
							// )
							// )
							// ? 1
							// : Number(
							// getStatusValue(
							// 'max_voltage_1'
							// )
							// )
						}
						name={'MaximalVoltage'}
						value={getNumericStatusValue('max_voltage_2')}
						measurement="Volt"
					/>
					<CHV
						text="VoltageAdjustment110"
						value={finderFC('adjust_volt_110_1')}
						value2={finderFC('adjust_volt_110_2')}
					/>
					<CHV
						text="VoltageAdjustment450"
						value={finderFC('adjust_volt_450_1')}
						value2={finderFC('adjust_volt_450_2')}
					/>
				</Flex>
				<Flex gap={10}>
					<CHM
						multiplier={
							1
							// isNaN(
							// Number(
							// getStatusValue('max_current_1')
							// )
							// )
							// ? 1
							// : Number(
							// getStatusValue(
							// 'max_current_1'
							// )
							// )
						}
						name={'MaximalCurrent'}
						value={getNumericStatusValue('max_current_2')}
						measurement="Amp"
					/>
					<CHV
						text="CurrentSensorAdjustment"
						value={finderFC('cur_sen_adj_1')}
						value2={finderFC('cur_sen_adj_2')}
					/>
					<CHV
						text="TempSensorAdjustment"
						value={finderFC('temp_sen_adj_1')}
						value2={finderFC('temp_sen_adj_2')}
					/>
				</Flex>
			</Flex>
		</Flex>
	)
}
